/**
 * AgentSettings — Agente CMDB: credenciales, distribución, operación, comandos,
 * mapeo de campos y bootstrap.
 *
 * QUÉ CAMBIA RESPECTO DE ANTES
 * ---------------------------------------------------------------------------
 * La sección tenía un marco propio sacado de otro sitio: un degradado oscuro a
 * mano (`#0f2027 → #2c5364`), un 🤖, píldoras de pestañas con emoji (🔑 🧷 🧭
 * 🗺 ⚡ 📦) y, debajo, un bloque de guía enorme plegado en la propia página. En
 * una app que ya tiene un estándar (cabecera de lista + pestañas `.dv-tabs-nav` +
 * tokens de color) eso cantaba mucho.
 *
 * Ahora: **la misma cabecera que una lista** (`.list-head`), pestañas con el
 * mismo patrón que las fichas (`.dv-tabs-nav` / `.dv-tab-btn`) y iconos de
 * heroicons en vez de emojis. La guía pasa a un diálogo, que es documentación y
 * no tiene por qué ocupar la pantalla.
 *
 * ORDEN DE LAS PESTAÑAS: por uso real, no por orden de creación
 * ---------------------------------------------------------------------------
 * Credenciales · Distribución · Operación · Comandos · Mapeo · Bootstrap
 *
 * DOS FALLOS QUE SE ARREGLARON EN EL BACKEND (aquí solo se ven, no se tocan)
 * ---------------------------------------------------------------------------
 *  1. `_require_admin()` de `blueprints/agent.py` no aceptaba el rol `owner` (el
 *     que emite el IdP para el dueño de la empresa) y el resto del CMDB sí: toda
 *     la sección respondía 403 —«Forbidden» en todas las pestañas— y no se podía
 *     subir ninguna versión.
 *  2. `GET /agent/mapping-profile` verificaba el token a mano con el secreto
 *     local; con la firma RS256 del IdP devolvía 401 y el cliente, al ver un 401,
 *     borra el token y manda al login. De ahí que al entrar en «Mapeo» la sección
 *     entera se quedara inservible hasta volver a entrar.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  CommandLineIcon,
  KeyIcon,
  ArrowUpTrayIcon,
  SignalIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
  RocketLaunchIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import { Panel, CommandBlock } from './common/Panel';
import AgentCredentialsList from './AgentCredentialsList';
import AgentBootstrapManager from './AgentBootstrapManager';
import AgentMappingProfile from './AgentMappingProfile';
import AgentCommandsPanel from './AgentCommandsPanel';
import AgentOperationsPanel from './AgentOperationsPanel';
import AgentNetworkPanel from './AgentNetworkPanel';
import SoftwarePackagesPanel from './SoftwarePackagesPanel';
// Publicar un artefacto también se puede haciendo drag & drop sobre la tarjeta.
import FileDropField from './common/FileDropField';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const QUICK_START_COMMAND = `pip install -r requirements.txt\npython agent.py daemon --url https://cmdb.tu-dominio.com --key <TU_API_KEY>`;

const SELF_COMMAND = `python agent.py self --url https://cmdb.tu-dominio.com --key <TU_API_KEY> --yes --output json`;

const DRY_RUN_COMMAND = `python agent.py self --url https://cmdb.tu-dominio.com --key <TU_API_KEY> --dry-run --output json --report-file .\\agent-self-report.json`;

const DAEMON_COMMAND = `python agent.py daemon --url https://cmdb.tu-dominio.com --key <TU_API_KEY>`;

const SWITCH_COMMAND = `python agent.py switch --ip 10.0.0.10 --community public --url https://cmdb.tu-dominio.com --key <TU_API_KEY> --yes`;

const HYPERVISOR_COMMAND = `python agent.py hypervisor --type vmware --host vcsa.midominio.local --user <USUARIO> --password <PASSWORD> --url https://cmdb.tu-dominio.com --key <TU_API_KEY>`;

const AUDIT_COMMAND = `python agent.py audit --service-id 12 --url https://cmdb.tu-dominio.com --key <TU_API_KEY>`;

const UPDATE_COMMAND = `python agent.py update --url https://cmdb.tu-dominio.com --key <TU_API_KEY>`;

const WINDOWS_UPDATE_COMMAND = `.venv\\Scripts\\Activate.ps1\npip install -r requirements.txt`;

const MST_COMMAND = `msiexec /i "CMDBAgent-<versión>.msi" /qn /norestart TRANSFORMS="CMDBAgent.mst" /L*v "C:\\ProgramData\\CMDBAgentDeploy\\agent.log"`;

const REMOTE_INSTALL_COMMAND = [
  "$pc = 'SRV01'",
  '$cred = Get-Credential',
  'Copy-Item ".\\CMDBAgent-2.2.4.msi" "\\\\$pc\\C$\\Windows\\Temp\\" -Credential $cred',
  'Invoke-Command -ComputerName $pc -Credential $cred -ScriptBlock {',
  "  $arg = '/i C:\\Windows\\Temp\\CMDBAgent-2.2.4.msi /qn /norestart /L*v C:\\Windows\\Temp\\cmdb-agent.log CMDB_URL=https://cmdb.tu-dominio.com CMDB_COMPANY_API_KEY=<api-key> CMDB_AGENT_ROLE=host'",
  '  Start-Process msiexec.exe -ArgumentList $arg -Wait -PassThru',
  '  Get-Service CMDBAgent',
  '}',
].join('\n');

const REMOTE_PSEXEC_COMMAND = `psexec.exe \\\\SRV01 -u DOMINIO\\admin -h -accepteula cmd /c msiexec /i "C:\\Windows\\Temp\\CMDBAgent-2.2.4.msi" /qn /norestart CMDB_URL="https://cmdb.tu-dominio.com" CMDB_COMPANY_API_KEY="<api-key>" CMDB_AGENT_ROLE="host"`;

const CAPABILITIES = [
  {
    title: 'Inventario local',
    text: 'Registra el propio servidor o puesto y actualiza fabricante, modelo, NICs, discos y storage.',
  },
  {
    title: 'Descubrimiento remoto',
    text: 'Puede registrar switches por SNMP o SSH e inventariar hypervisores con sus maquinas virtuales.',
  },
  {
    title: 'Ejecucion continua',
    text: 'En modo daemon hace heartbeat, refresca inventario y consulta comandos remotos pendientes.',
  },
  {
    title: 'Diagnostico seguro',
    text: 'Permite dry-run y report-file JSON para ver exactamente que detecta antes de escribir en CMDB.',
  },
];

const WORKFLOW = [
  'Genera una API key de empresa para autorizar el agente; no queda vinculada automaticamente a un CI.',
  'Si vas a desplegar el nuevo flujo seguro, genera un bootstrap token temporal y distribuye company_slug + bootstrap_token.',
  'Descarga el zip y ejecuta primero un self --dry-run para validar deteccion y mapping.',
  'Si el JSON del report-file es correcto, ejecuta self --yes o deja el agente en daemon.',
  'Cuando un agente quede comprometido o haya reinstalacion, reemite su credencial individual sin afectar al resto.',
  'Usa Mapeo de Campos si necesitas remapear atributos detectados hacia los modelos CMDB.',
  'Usa Comandos para relanzar rescans o ejecuciones remotas sobre agentes ya desplegados.',
  'Para actualizar el agente en un servidor remoto, ejecuta python agent.py update (no hace falta redescargar el zip).',
];

const MODES = [
  'self: registra la propia maquina y envia inventario base, componentes y storage.',
  'switch: descubre un switch por IP usando SNMP o SSH.',
  'hypervisor: registra el host virtualizador y sus VMs.',
  'daemon: mantiene al agente online, envia heartbeat y procesa comandos remotos.',
  'audit: envia informes de auditoria de Active Directory cuando aplica.',
  'update: descarga la ultima version del agente desde el servidor CMDB y se actualiza en sitio.',
];

const EXAMPLES = [
  {
    title: 'Alta rapida y funcionamiento continuo',
    description: 'Modo recomendado para un servidor que debe quedarse inventariado y recibiendo comandos.',
    command: QUICK_START_COMMAND,
  },
  {
    title: 'Daemon continuo',
    description: 'Mantiene heartbeat, inventario periodico y recepcion de comandos remotos.',
    command: DAEMON_COMMAND,
  },
  {
    title: 'Diagnostico seguro con report-file',
    description: 'No escribe en CMDB. Genera un JSON local para ver deteccion, warnings y payloads.',
    command: DRY_RUN_COMMAND,
  },
  {
    title: 'Alta unica del propio servidor',
    description: 'Registra la maquina actual y sube inventario sin dejar el daemon ejecutandose.',
    command: SELF_COMMAND,
  },
  {
    title: 'Switch remoto',
    description: 'Descubre un switch por IP usando SNMP o, si esta configurado, fallback por SSH.',
    command: SWITCH_COMMAND,
  },
  {
    title: 'Hypervisor',
    description: 'Registra el hypervisor y despues sus VMs detectadas.',
    command: HYPERVISOR_COMMAND,
  },
  {
    title: 'Auditoria AD',
    description: 'Envia informes de auditoria AD contra el servicio configurado en CMDB.',
    command: AUDIT_COMMAND,
  },
  {
    title: 'Actualizar agente',
    description: 'Descarga la ultima version desde el servidor CMDB y reemplaza los ficheros locales (preserva .env y .agent_id). Solo avanza de version: si la publicada es igual o mas antigua no hace nada; anade --force para reinstalarla.',
    command: UPDATE_COMMAND,
  },
];

const STORES_WHERE = [
  'Fabricante, modelo, nombre, serie y datos base se guardan en el CI principal.',
  'NICs y sus puertos se guardan como componentes del elemento.',
  'Discos, controladoras, RAID y volumenes se guardan en storage del servidor.',
  'El agente NO guarda copia local del inventario: el CMDB es la fuente de verdad.',
];

/**
 * Nota: `CopyButton`, `CommandBlock` y `Panel` vivían aquí, duplicados de los de
 * `AgentOperationsPanel` y del editor de mapeo. Ahora son compartidos
 * (`components/common/Panel.jsx`), con una sola tabla de estilos y tokens.
 */

/** Ficha de lo que hay publicado: versión, tamaño, fecha y hash. */
function ArtefactInfo({ info, empty }) {
  if (!info) return <p className="list-hint" style={{ margin: 0 }}>{empty}</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{info.filename}</span>
        {info.version && <span className="list-badge">{info.version}</span>}
        <span className="list-hint">
          {Math.round(((info.size || 0) / (1024 * 1024)) * 10) / 10} MB
        </span>
      </div>
      <span className="list-hint">
        Subido {info.uploaded_at ? new Date(info.uploaded_at).toLocaleString() : '—'}
      </span>
      {info.sha256 && (
        <span className="list-hint list-code" style={{ wordBreak: 'break-all' }}>
          SHA-256 {info.sha256}
        </span>
      )}
      {info.notes && <span className="list-hint">{info.notes}</span>}
    </div>
  );
}

/**
 * Distribución — de aquí sale lo que se instala en los servidores.
 *
 * El MSI, el EXE y el paquete (.zip) son **globales del CMDB**, no de una
 * empresa: por eso el backend no les pide `company_id`. El MSI y el EXE son
 * artefactos distintos (WiX y NSIS) y cada uno tiene su ranura.
 *
 * Todo lo que se publique aquí aparece —sin necesidad de sesión— en la página
 * pública `/instalar-agente`, que es la que se pasa a quien va a instalar.
 */
function DistribucionTab({
  msiInfo, installerInfo, packageInfo,
  msiVersion, setMsiVersion, msiNotes, setMsiNotes,
  installerVersion, setInstallerVersion, installerNotes, setInstallerNotes,
  packageVersion, setPackageVersion, packageNotes, setPackageNotes,
  installerError, msiSaving, installerSaving, packageSaving, msiStatus, installerStatus, packageStatus,
  msiInputRef, fileInputRef, packageInputRef,
  msiReset, installerReset, packageReset,
  onMsiDownload, onMsiUpload,
  onInstallerDownload, onInstallerUpload, onPackageDownload, onPackageUpload,
}) {
  // Misma página, servida por este CMDB. Se enseña para poder pasarla tal cual.
  const publicUrl = `${window.location.origin}/instalar-agente`;

  return (
    <>
      <div className="row g-3">
        <div className="col-lg-6">
          <Panel
            title="Instalador (.msi)"
            hint="WiX: el que se despliega en silencio, por GPO o con Intune. No tiene asistente visual: se le pasan URL y clave como propiedades."
            actions={msiInfo && (
              <button type="button" className="list-btn-secondary" onClick={onMsiDownload}>
                <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Descargar
              </button>
            )}
          >
            <ArtefactInfo info={msiInfo} empty="Todavía no hay ningún MSI publicado." />

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FileDropField
                key={`msi-${msiReset}`}
                inputRef={msiInputRef}
                accept=".msi"
                hint="Fichero .msi (WiX)"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <label className="list-hint" style={{ flex: 1 }}>
                  Versión
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={msiVersion}
                    onChange={(e) => setMsiVersion(e.target.value)}
                    placeholder="2.2.4"
                  />
                </label>
                <label className="list-hint" style={{ flex: 2 }}>
                  Notas
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={msiNotes}
                    onChange={(e) => setMsiNotes(e.target.value)}
                    placeholder="MSI firmado para despliegue masivo"
                  />
                </label>
              </div>
              <button type="button" className="list-btn-primary" onClick={onMsiUpload} disabled={msiSaving}>
                <ArrowUpTrayIcon style={{ width: 14, height: 14 }} />
                {msiSaving ? ' Subiendo…' : ' Publicar MSI'}
              </button>
            </div>

            {msiStatus && (
              <p className="list-badge list-badge--on" style={{ marginTop: 10, display: 'inline-block' }}>{msiStatus}</p>
            )}
          </Panel>
        </div>

        <div className="col-lg-6">
          <Panel
            title="Instalador (.exe)"
            hint="NSIS: el ejecutable con el que se instala el agente en un Windows nuevo. Abre el panel para configurar URL y clave a mano."
            actions={installerInfo && (
              <button type="button" className="list-btn-secondary" onClick={onInstallerDownload}>
                <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Descargar
              </button>
            )}
          >
            <ArtefactInfo info={installerInfo} empty="Todavía no hay ningún instalador publicado." />

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FileDropField
                key={`exe-${installerReset}`}
                inputRef={fileInputRef}
                accept=".exe"
                hint="Fichero .exe (NSIS)"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <label className="list-hint" style={{ flex: 1 }}>
                  Versión
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={installerVersion}
                    onChange={(e) => setInstallerVersion(e.target.value)}
                    placeholder="2.0.1"
                  />
                </label>
                <label className="list-hint" style={{ flex: 2 }}>
                  Notas
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={installerNotes}
                    onChange={(e) => setInstallerNotes(e.target.value)}
                    placeholder="Build estable con el arreglo del updater"
                  />
                </label>
              </div>
              <button type="button" className="list-btn-primary" onClick={onInstallerUpload} disabled={installerSaving}>
                <ArrowUpTrayIcon style={{ width: 14, height: 14 }} />
                {installerSaving ? ' Subiendo…' : ' Publicar instalador'}
              </button>
            </div>

            {installerStatus && (
              <p className="list-badge list-badge--on" style={{ marginTop: 10, display: 'inline-block' }}>{installerStatus}</p>
            )}
          </Panel>
        </div>

        <div className="col-lg-6">
          <Panel
            title="Paquete del agente (.zip)"
            hint="El ZIP con el código del agente, que es lo que descargan los servidores y lo que usa el auto-update."
            actions={packageInfo && (
              <button type="button" className="list-btn-secondary" onClick={onPackageDownload}>
                <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Descargar
              </button>
            )}
          >
            <ArtefactInfo info={packageInfo} empty="Todavía no hay ningún paquete publicado." />

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FileDropField
                key={`zip-${packageReset}`}
                inputRef={packageInputRef}
                accept=".zip"
                hint="Fichero .zip con el código del agente"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <label className="list-hint" style={{ flex: 1 }}>
                  Versión
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={packageVersion}
                    onChange={(e) => setPackageVersion(e.target.value)}
                    placeholder="2.0.1"
                  />
                </label>
                <label className="list-hint" style={{ flex: 2 }}>
                  Notas
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={packageNotes}
                    onChange={(e) => setPackageNotes(e.target.value)}
                    placeholder="ZIP firmado para entrega"
                  />
                </label>
              </div>
              <button type="button" className="list-btn-primary" onClick={onPackageUpload} disabled={packageSaving}>
                <ArrowUpTrayIcon style={{ width: 14, height: 14 }} />
                {packageSaving ? ' Subiendo…' : ' Publicar paquete'}
              </button>
            </div>

            {packageStatus && (
              <p className="list-badge list-badge--on" style={{ marginTop: 10, display: 'inline-block' }}>{packageStatus}</p>
            )}
          </Panel>
        </div>

        <div className="col-lg-6">
          <Panel
            title="Página pública de descargas"
            hint="Se pasa a quien vaya a instalar el agente. No pide iniciar sesión: solo descarga los ficheros publicados aquí."
            actions={(
              <a className="list-btn-secondary" style={{ textDecoration: 'none' }} href="/instalar-agente" target="_blank" rel="noreferrer">
                <ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} /> Abrir
              </a>
            )}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <code
                className="list-code"
                style={{
                  flex: '1 1 240px', padding: '6px 8px',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-input)', wordBreak: 'break-all',
                }}
              >
                {publicUrl}
              </code>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => navigator.clipboard?.writeText(publicUrl)}
              >
                <DocumentDuplicateIcon style={{ width: 14, height: 14 }} /> Copiar
              </button>
            </div>
            <ul className="list-hint" style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              <li>MSI y EXE: instalación en Windows. El MSI admite modo silencioso.</li>
              <li>ZIP: agentes Linux y actualización manual.</li>
              <li>La página enseña versión, tamaño y SHA-256 de lo publicado.</li>
            </ul>
          </Panel>
        </div>
      </div>

      {installerError && (
        <div className="alert alert-danger py-2" style={{ marginBottom: 16 }}>{installerError}</div>
      )}

      <Panel
        title="Actualizar un agente ya instalado"
        hint="Cambiar los ficheros en el repositorio NO actualiza lo que ya corre en un servidor: hay que pedírselo al agente (pestaña Comandos) o ejecutar su actualización en el propio equipo."
      >
        <CommandBlock command={UPDATE_COMMAND} />
        <p className="list-hint" style={{ margin: '10px 0 0' }}>
          En Windows, si se ha actualizado el código a mano:
        </p>
        <CommandBlock command={WINDOWS_UPDATE_COMMAND} />
      </Panel>
    </>
  );
}

/** Guía de instalación: es documentación, así que va en un diálogo. */
function GuiaDialog({ onClose }) {
  const [open, setOpen] = useState('workflow');
  const Section = ({ id, title, children }) => (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(open === id ? null : id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.86rem', textAlign: 'left',
        }}
      >
        {open === id ? <ChevronUpIcon style={{ width: 14, height: 14 }} /> : <ChevronDownIcon style={{ width: 14, height: 14 }} />}
        {title}
      </button>
      {open === id && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );

  return (
    <Dialog
      title="Guía del agente"
      icon={<BookOpenIcon style={{ width: 18, height: 18 }} />}
      maxWidth={780}
      onClose={onClose}
      footer={(
        <button type="button" className="list-btn-secondary" onClick={onClose}>Cerrar</button>
      )}
    >
      <Section id="workflow" title="Flujo recomendado">
        <ol className="list-hint" style={{ margin: 0, paddingLeft: 18 }}>
          {WORKFLOW.map((step) => <li key={step} style={{ marginBottom: 6 }}>{step}</li>)}
        </ol>
      </Section>

      <Section id="modes" title="Modos de ejecución">
        <ul className="list-hint" style={{ margin: 0, paddingLeft: 18 }}>
          {MODES.map((mode) => <li key={mode} style={{ marginBottom: 4 }}>{mode}</li>)}
        </ul>
      </Section>

      <Section id="capabilities" title="Qué puede descubrir">
        <div className="row g-2">
          {CAPABILITIES.map((item) => (
            <div className="col-md-6" key={item.title}>
              <div style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', height: '100%' }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{item.title}</div>
                <span className="list-hint">{item.text}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="stores" title="Dónde se guarda lo que detecta">
        <ul className="list-hint" style={{ margin: 0, paddingLeft: 18 }}>
          {STORES_WHERE.map((item) => <li key={item} style={{ marginBottom: 4 }}>{item}</li>)}
        </ul>
      </Section>

      <Section id="mst" title="Instalar por GPO: crear el MST">
        <p className="list-hint" style={{ margin: '0 0 8px' }}>
          El MST solo hace falta si despliegas por <strong>Directiva de grupo → Instalación de
          software</strong> en modo <em>Asignado</em>: ahí no se pueden pasar propiedades por línea de
          comandos. Con un script, PsExec, SCCM o Intune, pasa <code>CMDB_URL</code>,{' '}
          <code>CMDB_COMPANY_API_KEY</code> y <code>CMDB_AGENT_ROLE</code> directamente y no lo necesitas.
        </p>
        <ol className="list-hint" style={{ margin: 0, paddingLeft: 18 }}>
          <li style={{ marginBottom: 4 }}>Programas: <strong>InstEd</strong> (gratis, instedit.com; recomendado), <strong>Orca</strong> (Windows SDK → MSI Tools), SuperOrca o msitran/WiX por consola.</li>
          <li style={{ marginBottom: 4 }}>InstEd: <em>File → Open</em> → el MSI (no se modifica) → <em>Transform → New Transform</em>.</li>
          <li style={{ marginBottom: 4 }}>Tabla <strong>Property</strong> → añade <code>CMDB_URL</code>, <code>CMDB_COMPANY_API_KEY</code> y <code>CMDB_AGENT_ROLE</code> (host o network).</li>
          <li style={{ marginBottom: 4 }}><em>Transform → Save Transform As</em> → <code>CMDBAgent.mst</code>. Cierra sin guardar el MSI. En Orca es igual, terminando en <em>Generate Transform</em>.</li>
          <li style={{ marginBottom: 4 }}>GPO: copia MSI y MST a una ruta UNC → GPMC → <em>Computer Configuration → Policies → Software Settings → Software installation</em> → New → Package (Assigned) → pestaña <strong>Modifications</strong> → añade el MST.</li>
        </ol>
        <CommandBlock command={MST_COMMAND} />
        <p className="list-hint" style={{ margin: '8px 0 0' }}>
          <strong>El MST lleva la API key en claro</strong>: guárdalo en una ruta protegida, no lo subas
          al repositorio y revoca la clave si se filtra.
        </p>
      </Section>

      <Section id="remoto" title="Instalar en un equipo remoto">
        <p className="list-hint" style={{ margin: '0 0 8px' }}>
          Con una cuenta de administrador en el destino y WinRM habilitado (5985/5986), o SMB/RPC para
          PsExec. Copia el MSI al equipo antes de instalar: si se instala desde una ruta UNC con PsExec,
          el proceso corre como SYSTEM y el permiso sobre el recurso compartido puede fallar.
        </p>
        <CommandBlock command={REMOTE_INSTALL_COMMAND} />
        <p className="list-hint" style={{ margin: '8px 0 0' }}>Con PsExec (sin WinRM):</p>
        <CommandBlock command={REMOTE_PSEXEC_COMMAND} />
        <p className="list-hint" style={{ margin: '8px 0 0' }}>
          Comprueba el servicio y el log (<em>Installation success or error status: 0</em>). Códigos:
          0 correcto, 3010 correcto con reinicio, 1603 error grave, 1619 no se pudo abrir el paquete.
          Actualizar = el mismo comando con el MSI nuevo y <strong>sin propiedades</strong> (conserva
          el <code>.env</code>); desinstalar = <code>msiexec /x "CMDBAgent-2.2.4.msi" /qn</code>.
        </p>
        <p className="list-hint" style={{ margin: '8px 0 0' }}>
          La página pública <strong>/instalar-agente</strong> tiene estas mismas instrucciones y un
          generador que descarga el <code>.cmd</code>, el <code>.ps1</code> remoto y un <code>.ps1</code>{' '}
          que crea el MST con la URL, la clave y el rol (todo se genera en el navegador).
        </p>
      </Section>

      <Section id="examples" title="Ejemplos de línea de comandos">
        {EXAMPLES.map((example) => (
          <div key={example.title} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{example.title}</div>
            <span className="list-hint">{example.description}</span>
            <CommandBlock command={example.command} />
          </div>
        ))}
      </Section>
    </Dialog>
  );
}

/* Pestañas, en orden de uso. Los `id` no cambian (no rompen nada de fuera). */
const TABS = [
  { id: 'keys',         label: 'Credenciales',  Icon: KeyIcon },
  { id: 'network',      label: 'Red',           Icon: GlobeAltIcon },
  { id: 'packages',     label: 'Paquetes',      Icon: ArchiveBoxIcon },
  { id: 'distribucion', label: 'Distribución',  Icon: ArrowUpTrayIcon },
  { id: 'operations',   label: 'Operación',     Icon: SignalIcon },
  { id: 'commands',     label: 'Comandos',      Icon: BoltIcon },
  { id: 'mapping',      label: 'Mapeo',         Icon: ArrowsRightLeftIcon },
  { id: 'bootstrap',    label: 'Bootstrap',     Icon: RocketLaunchIcon },
];

export default function AgentSettings({ companyId }) {
  const [activeTab, setActiveTab] = useState('keys');
  const [msiInfo, setMsiInfo] = useState(null);
  const [installerInfo, setInstallerInfo] = useState(null);
  const [packageInfo, setPackageInfo] = useState(null);
  const [msiVersion, setMsiVersion] = useState('');
  const [msiNotes, setMsiNotes] = useState('');
  const [installerVersion, setInstallerVersion] = useState('');
  const [installerNotes, setInstallerNotes] = useState('');
  const [packageVersion, setPackageVersion] = useState('');
  const [packageNotes, setPackageNotes] = useState('');
  const [installerError, setInstallerError] = useState('');
  const [msiSaving, setMsiSaving] = useState(false);
  const [installerSaving, setInstallerSaving] = useState(false);
  const [packageSaving, setPackageSaving] = useState(false);
  const [msiStatus, setMsiStatus] = useState('');
  const [installerStatus, setInstallerStatus] = useState('');
  const [packageStatus, setPackageStatus] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  // Tras publicar, el campo de fichero se remonta vacío (ver FileDropField).
  const [msiReset, setMsiReset] = useState(0);
  const [installerReset, setInstallerReset] = useState(0);
  const [packageReset, setPackageReset] = useState(0);
  const msiInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const packageInputRef = useRef(null);

  const msiTopVersion = msiInfo?.version || 'sin publicar';
  const installerTopVersion = installerInfo?.version || 'sin publicar';
  const packageTopVersion = packageInfo?.version || 'sin publicar';

  useEffect(() => {
    api.getAgentMsiInfo()
      .then((res) => setMsiInfo(res.msi || null))
      .catch(() => setMsiInfo(null));
    api.getAgentInstallerInfo()
      .then((res) => setInstallerInfo(res.installer || null))
      .catch(() => setInstallerInfo(null));
    api.getAgentPackageInfo()
      .then((res) => setPackageInfo(res.package || null))
      .catch(() => setPackageInfo(null));
  }, []);

  const bajarBlob = (url, nombre, onError) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`No disponible (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = nombre;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => onError(e.message));
  };

  const handleDownload = () => bajarBlob(
    `${API_BASE}/agent/download`,
    'cmdb-agent.zip',
    (m) => setInstallerError(`Error al descargar el agente: ${m}`),
  );

  const handleInstallerDownload = () => bajarBlob(
    `${API_BASE}/agent/installer/download`,
    installerInfo?.filename || 'cmdb-agent-installer.exe',
    (m) => setInstallerError(`Error al descargar el instalador: ${m}`),
  );

  const handleMsiDownload = () => bajarBlob(
    `${API_BASE}/agent/msi/download`,
    msiInfo?.filename || 'cmdb-agent-installer.msi',
    (m) => setInstallerError(`Error al descargar el MSI: ${m}`),
  );

  const handleMsiUpload = async () => {
    const file = msiInputRef.current?.files?.[0];
    if (!file) { setInstallerError('Selecciona un fichero .msi'); return; }
    setMsiSaving(true);
    setInstallerError('');
    setMsiStatus('');
    try {
      const res = await api.uploadAgentMsi(file, { version: msiVersion, notes: msiNotes });
      setMsiInfo(res.msi || null);
      setMsiStatus('MSI publicado');
      setMsiVersion('');
      setMsiNotes('');
      if (msiInputRef.current) msiInputRef.current.value = '';
      setMsiReset((n) => n + 1);
    } catch (e) {
      setInstallerError(e.message || 'No se pudo subir el MSI');
    } finally {
      setMsiSaving(false);
    }
  };

  const handlePackageDownload = () => bajarBlob(
    `${API_BASE}/agent/package/download`,
    packageInfo?.filename || 'cmdb-agent-package.zip',
    (m) => setInstallerError(`Error al descargar el paquete: ${m}`),
  );

  const handleInstallerUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) { setInstallerError('Selecciona un fichero .exe'); return; }
    setInstallerSaving(true);
    setInstallerError('');
    setInstallerStatus('');
    try {
      const res = await api.uploadAgentInstaller(file, { version: installerVersion, notes: installerNotes });
      setInstallerInfo(res.installer || null);
      setInstallerStatus('Instalador publicado');
      setInstallerVersion('');
      setInstallerNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setInstallerReset((n) => n + 1);
    } catch (e) {
      setInstallerError(e.message || 'No se pudo subir el instalador');
    } finally {
      setInstallerSaving(false);
    }
  };

  const handlePackageUpload = async () => {
    const file = packageInputRef.current?.files?.[0];
    if (!file) { setInstallerError('Selecciona un fichero .zip'); return; }
    setPackageSaving(true);
    setInstallerError('');
    setPackageStatus('');
    try {
      const res = await api.uploadAgentPackage(file, { version: packageVersion, notes: packageNotes });
      setPackageInfo(res.package || null);
      setPackageStatus('Paquete publicado');
      setPackageVersion('');
      setPackageNotes('');
      if (packageInputRef.current) packageInputRef.current.value = '';
      setPackageReset((n) => n + 1);
    } catch (e) {
      setInstallerError(e.message || 'No se pudo subir el paquete');
    } finally {
      setPackageSaving(false);
    }
  };

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          Selecciona una empresa para gestionar el agente.
        </div>
      </div>
    );
  }

  return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      {/* ══ Cabecera, la misma que la de una lista ══ */}
      <div className="list-head">
        <div className="list-head-left">
          <CommandLineIcon className="list-head-icon" />
          <div>
            <h1 className="list-title">Agente CMDB</h1>
            <p className="list-subtitle">
              Credenciales del agente, versiones publicadas, mapeo de campos y comandos remotos.
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          <span className="list-badge" title="Versión del MSI publicada">MSI {msiTopVersion}</span>
          <span className="list-badge" title="Versión del instalador EXE publicada">EXE {installerTopVersion}</span>
          <span className="list-badge" title="Versión del paquete del agente publicado">ZIP {packageTopVersion}</span>
          <a className="list-btn-secondary" style={{ textDecoration: 'none' }} href="/instalar-agente" target="_blank" rel="noreferrer">
            <ArrowTopRightOnSquareIcon style={{ width: 15, height: 15 }} /> Página pública
          </a>
          <button type="button" className="list-btn-secondary" onClick={() => setGuideOpen(true)}>
            <BookOpenIcon style={{ width: 15, height: 15 }} /> Guía
          </button>
          <button type="button" className="list-btn-secondary" onClick={handleDownload}>
            <ArrowDownTrayIcon style={{ width: 15, height: 15 }} /> Descargar agente
          </button>
        </div>
      </div>

      {/* ══ Pestañas, el mismo patrón que las fichas ══ */}
      <div className="dv-tabs-nav" style={{ marginBottom: 16 }}>
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            className={`dv-tab-btn${activeTab === id ? ' active' : ''}`}
            onClick={() => setActiveTab(id)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon style={{ width: 15, height: 15 }} />
            {label}
          </button>
        ))}
      </div>

      {/* ══ Contenido ══ */}
      {activeTab === 'keys' && <AgentCredentialsList companyId={companyId} />}
      {activeTab === 'network' && <AgentNetworkPanel companyId={companyId} />}
      {activeTab === 'packages' && (
        <div className="list-page--embedded">
          <SoftwarePackagesPanel companyId={companyId} />
        </div>
      )}
      {activeTab === 'distribucion' && (
        <DistribucionTab
          msiInfo={msiInfo}
          installerInfo={installerInfo}
          packageInfo={packageInfo}
          msiVersion={msiVersion}
          setMsiVersion={setMsiVersion}
          msiNotes={msiNotes}
          setMsiNotes={setMsiNotes}
          installerVersion={installerVersion}
          setInstallerVersion={setInstallerVersion}
          installerNotes={installerNotes}
          setInstallerNotes={setInstallerNotes}
          packageVersion={packageVersion}
          setPackageVersion={setPackageVersion}
          packageNotes={packageNotes}
          setPackageNotes={setPackageNotes}
          installerError={installerError}
          msiSaving={msiSaving}
          installerSaving={installerSaving}
          packageSaving={packageSaving}
          msiStatus={msiStatus}
          installerStatus={installerStatus}
          packageStatus={packageStatus}
          msiInputRef={msiInputRef}
          fileInputRef={fileInputRef}
          packageInputRef={packageInputRef}
          msiReset={msiReset}
          installerReset={installerReset}
          packageReset={packageReset}
          onMsiDownload={handleMsiDownload}
          onMsiUpload={handleMsiUpload}
          onInstallerDownload={handleInstallerDownload}
          onInstallerUpload={handleInstallerUpload}
          onPackageDownload={handlePackageDownload}
          onPackageUpload={handlePackageUpload}
        />
      )}
      {activeTab === 'operations' && <AgentOperationsPanel companyId={companyId} />}
      {activeTab === 'commands' && <AgentCommandsPanel companyId={companyId} />}
      {activeTab === 'mapping' && <AgentMappingProfile companyId={companyId} />}
      {activeTab === 'bootstrap' && (
        <AgentBootstrapManager
          companyId={companyId}
          onGoToCredentials={() => setActiveTab('keys')}
        />
      )}

      {guideOpen && <GuiaDialog onClose={() => setGuideOpen(false)} />}
    </div>
  );
}
